import { baseApi } from "./baseApi";
import type { ApiResponse, RoleTemplate } from "../../types";

export interface CreateRoleTemplateData {
  name: string;
  description?: string | null;
  permissionCodes: string[];
}

export interface UpdateRoleTemplateData {
  id: string;
  data: {
    name?: string;
    /** `null` clears a previously saved description; omitting the key leaves it unchanged. */
    description?: string | null;
    permissionCodes?: string[];
  };
}

export const roleTemplateApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getRoleTemplates: builder.query<ApiResponse<RoleTemplate[]>, void>({
      query: () => "/role-templates",
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(template => ({ type: "RoleTemplate" as const, id: template.id })),
              { type: "RoleTemplate" as const, id: "LIST" },
            ]
          : [{ type: "RoleTemplate" as const, id: "LIST" }],
    }),

    createRoleTemplate: builder.mutation<ApiResponse<RoleTemplate>, CreateRoleTemplateData>({
      query: body => ({ url: "/role-templates", method: "POST", body }),
      invalidatesTags: [{ type: "RoleTemplate", id: "LIST" }],
    }),

    updateRoleTemplate: builder.mutation<ApiResponse<RoleTemplate>, UpdateRoleTemplateData>({
      query: ({ id, data }) => ({ url: `/role-templates/${id}`, method: "PUT", body: data }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "RoleTemplate", id },
        { type: "RoleTemplate", id: "LIST" },
      ],
    }),

    deleteRoleTemplate: builder.mutation<ApiResponse<void>, string>({
      query: id => ({ url: `/role-templates/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "RoleTemplate", id },
        { type: "RoleTemplate", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetRoleTemplatesQuery,
  useCreateRoleTemplateMutation,
  useUpdateRoleTemplateMutation,
  useDeleteRoleTemplateMutation,
} = roleTemplateApi;
